import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import butterflyApi from '../ButterflyApi';
import MobilePage from '../UiComponents/MobilePage';
import { isLogin } from '../Utils/Cookies';
import {
  INQUIRY_CATEGORIES,
  getApiErrorMessage,
  getHeroName,
  getLocalHeroes,
} from './supportInquiryUtils';
import './SupportInquiry.css';

const SupportInquiryNew = () => {
  const navigate = useNavigate();
  const [category, setCategory] = useState('general')
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [heroNo, setHeroNo] = useState('')
  const [heroes, setHeroes] = useState(getLocalHeroes())
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const loadHeroes = useCallback(async () => {
    if (!isLogin()) {
      setLoading(false)
      return
    }

    setLoading(true)
    const response = await butterflyApi.getHeroes(() => {
      setHeroes(getLocalHeroes())
    })

    if (response && response.items) {
      localStorage.setItem('heroes', JSON.stringify(response.items))
      setHeroes(response.items)
    }
    setLoading(false)
  }, [])

  const submit = async () => {
    const trimmedTitle = title.trim()
    const trimmedContent = content.trim()

    if (!trimmedTitle || trimmedTitle.length > 100) {
      setErrorMessage('문의 제목은 1자 이상 100자 이하로 입력해주세요.')
      return
    }

    if (!trimmedContent) {
      setErrorMessage('문의 내용을 입력해주세요.')
      return
    }

    setSubmitting(true)
    setErrorMessage('')
    const inquiry = await butterflyApi.createSupportInquiry(
      category,
      trimmedTitle,
      trimmedContent,
      heroNo ? Number(heroNo) : null,
      error => {
        setErrorMessage(getApiErrorMessage(error, '문의를 등록하지 못했습니다.'))
      },
    )
    setSubmitting(false)

    if (inquiry && inquiry.inquiryNo) {
      navigate(`/support/inquiries/${inquiry.inquiryNo}`)
    }
  }

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 })
    loadHeroes()
  }, [loadHeroes])

  if (loading) {
    return <MobilePage className='storybook-page inquiry-page' loading loadingMessage="문의 화면을 준비하고 있습니다." />
  }

  if (!isLogin()) {
    return <MobilePage className='storybook-page inquiry-page'>
      <main className='inquiry-content'>
        <Link className='book-text-link inquiry-back' to='/support/inquiries'><span aria-hidden='true'>‹</span> 문의 내역</Link>
        <header className='inquiry-intro'>
          <p className='book-eyebrow'><span aria-hidden='true'>✦</span> 텔링파파에게 남기는 이야기</p>
          <h1 className='book-title'>문의 작성</h1>
          <p className='book-description'>어떤 도움이 필요하신가요?<br />궁금한 점을 편하게 남겨주세요.</p>
        </header>
        <section className='inquiry-empty' aria-labelledby='inquiry-new-login-title'>
          <span className='inquiry-empty-icon' aria-hidden='true'>✧</span>
          <h2 id='inquiry-new-login-title'>로그인하고 이야기를 남겨주세요</h2>
          <p>로그인하면 문의를 작성하고<br />문의 내역에서 답변을 확인할 수 있어요.</p>
          <Link className='book-primary-button' to='/login?redirect=/support/inquiries/new'>로그인하기 <span aria-hidden='true'>→</span></Link>
        </section>
      </main>
    </MobilePage>
  }

  return <MobilePage className='storybook-page inquiry-page'>
    <main className={`inquiry-content inquiry-content--new${errorMessage ? ' inquiry-content--has-error' : ''}`}>
      <Link className='book-text-link inquiry-back' to='/support/inquiries'><span aria-hidden='true'>‹</span> 문의 내역</Link>
      <header className='inquiry-intro'>
        <p className='book-eyebrow'><span aria-hidden='true'>✦</span> 텔링파파에게 남기는 이야기</p>
        <h1 className='book-title'>문의 작성</h1>
        <p className='book-description'>어떤 도움이 필요하신가요?<br />내용을 남겨주시면 확인 후 답변드릴게요.</p>
      </header>

      <form
        id='support-inquiry-form'
        className='inquiry-form'
        noValidate
        aria-busy={submitting}
        onSubmit={event => {
          event.preventDefault()
          if (!submitting) submit()
        }}
      >
        <section className='inquiry-form-panel' aria-labelledby='inquiry-info-title'>
          <div className='inquiry-form-heading'>
            <span aria-hidden='true'>01</span><h2 id='inquiry-info-title'>문의 정보</h2>
          </div>
          <p className='inquiry-form-hint'>내용에 맞는 유형을 골라주세요.</p>
          <div className='inquiry-fields'>
            <div className='inquiry-field'>
              <label htmlFor='inquiry-category'>문의 유형</label>
              <select
                id='inquiry-category'
                className='inquiry-control'
                value={category}
                disabled={submitting}
                onChange={event => setCategory(event.target.value)}
              >
                {INQUIRY_CATEGORIES.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
              </select>
            </div>

            <div className='inquiry-field'>
              <label htmlFor='inquiry-hero'>관련 주인공 <span className='inquiry-optional'>선택</span></label>
              <select
                id='inquiry-hero'
                className='inquiry-control'
                value={heroNo}
                disabled={submitting}
                aria-describedby='inquiry-hero-hint'
                onChange={event => setHeroNo(event.target.value)}
              >
                <option value=''>선택 안 함</option>
                {heroes.map(hero => <option key={hero.heroNo} value={hero.heroNo}>{getHeroName(hero)}</option>)}
              </select>
              <p className='inquiry-field-hint' id='inquiry-hero-hint'>동화나 주인공에 대한 문의라면 선택해 주세요.</p>
            </div>
          </div>
        </section>

        <section className='inquiry-form-panel' aria-labelledby='inquiry-message-title'>
          <div className='inquiry-form-heading'>
            <span aria-hidden='true'>02</span><h2 id='inquiry-message-title'>문의 내용</h2>
          </div>
          <p className='inquiry-form-hint'>확인이 필요한 내용을 자세히 알려주세요.</p>
          <div className='inquiry-fields'>
            <div className='inquiry-field'>
              <label htmlFor='inquiry-title'>제목 <span className='inquiry-required'>필수</span></label>
              <input
                id='inquiry-title'
                type='text'
                className='inquiry-control'
                value={title}
                required
                maxLength={100}
                disabled={submitting}
                aria-describedby='inquiry-title-count'
                onChange={event => setTitle(event.target.value)}
                placeholder='궁금한 점을 한 줄로 적어주세요'
              />
              <span className='inquiry-title-count' id='inquiry-title-count'>{title.length} / 100자</span>
            </div>

            <div className='inquiry-field'>
              <label htmlFor='inquiry-message'>내용 <span className='inquiry-required'>필수</span></label>
              <textarea
                id='inquiry-message'
                className='inquiry-control inquiry-textarea'
                value={content}
                rows={7}
                required
                disabled={submitting}
                onChange={event => setContent(event.target.value)}
                placeholder='어떤 상황에서 도움이 필요하셨나요? 궁금한 점이나 확인이 필요한 내용을 남겨주세요.'
              />
            </div>
          </div>
        </section>
      </form>
    </main>

    <div className='inquiry-submit-bar'>
      {errorMessage
        ? <div className='book-error inquiry-submit-error' id='inquiry-submit-error' role='alert'>{errorMessage}</div>
        : <p>답변은 문의 내역에서 확인할 수 있어요.</p>}
      <button
        type='submit'
        form='support-inquiry-form'
        className='book-primary-button'
        disabled={submitting}
        aria-describedby={errorMessage ? 'inquiry-submit-error' : undefined}
      >
        {submitting ? '등록 중...' : '문의 등록하기'} <span aria-hidden='true'>→</span>
      </button>
    </div>
  </MobilePage>
}

export default SupportInquiryNew
